import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const useStreak = () => {
  const { user } = useAuth();
  const [currentStreak, setCurrentStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setCurrentStreak(0);
      setLoading(false);
      return;
    }

    fetchStreak();
  }, [user]);

  const fetchStreak = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('daily_streak, last_checkin_date')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching streak:', error);
        setCurrentStreak(0);
      } else if (data) {
        // Check if streak is still valid (last check-in was yesterday or today)
        const lastCheckin = data.last_checkin_date ? new Date(data.last_checkin_date) : null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (lastCheckin) {
          const lastCheckinDate = new Date(lastCheckin);
          lastCheckinDate.setHours(0, 0, 0, 0);
          const daysDiff = Math.floor((today.getTime() - lastCheckinDate.getTime()) / (1000 * 60 * 60 * 24));

          if (daysDiff > 1) {
            // Streak broken, reset it
            await resetStreak();
          } else {
            setCurrentStreak(data.daily_streak || 0);
          }
        } else {
          setCurrentStreak(0);
        }
      } else {
        setCurrentStreak(0);
      }
    } catch (error) {
      console.error('Error in fetchStreak:', error);
      setCurrentStreak(0);
    } finally {
      setLoading(false);
    }
  };

  const updateStreak = async () => {
    if (!user) return;

    try {
      const { data: settings } = await supabase
        .from('user_settings')
        .select('daily_streak, last_checkin_date')
        .eq('user_id', user.id)
        .single();

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];

      const lastCheckin = settings?.last_checkin_date ? new Date(settings.last_checkin_date) : null;
      
      if (lastCheckin) {
        const lastCheckinDate = new Date(lastCheckin);
        lastCheckinDate.setHours(0, 0, 0, 0);
        const lastCheckinStr = lastCheckinDate.toISOString().split('T')[0];

        // Don't increment if already checked in today
        if (lastCheckinStr === todayStr) {
          return;
        }
      }

      const newStreak = (settings?.daily_streak || 0) + 1;

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          daily_streak: newStreak,
          last_checkin_date: todayStr,
        });

      if (error) {
        console.error('Error updating streak:', error);
      } else {
        setCurrentStreak(newStreak);
      }
    } catch (error) {
      console.error('Error in updateStreak:', error);
    }
  };

  const resetStreak = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_settings')
        .update({ daily_streak: 0 })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error resetting streak:', error);
      } else {
        setCurrentStreak(0);
      }
    } catch (error) {
      console.error('Error in resetStreak:', error);
    }
  };

  return {
    currentStreak,
    loading,
    updateStreak,
    refetch: fetchStreak,
  };
};
